import {
  type FieldValues,
  type Path,
  useFormContext,
  useFormState,
} from 'react-hook-form';

import TextField, { type TextFieldProps } from '../TextField/TextField';

export type RhfTextFieldProps<T extends FieldValues> = Omit<
  TextFieldProps,
  'name' | 'error' | 'helperText'
> & {
  name: Path<T>;
  // Shown when the field has no validation error.
  helperText?: TextFieldProps['helperText'];
  // Transform applied to the raw input before it is written to form state
  setValueAs?: (value: string) => unknown;
};

export default function RhfTextField<T extends FieldValues>({
  name,
  helperText,
  setValueAs,
  ...rest
}: RhfTextFieldProps<T>) {
  const { register, control } = useFormContext<T>();
  const { errors } = useFormState({ control, name });
  const fieldError = errors[name];
  const errorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;
  const invalid = Boolean(fieldError);
  const { ref, ...reg } = register(
    name,
    setValueAs ? { setValueAs } : undefined
  );
  return (
    <TextField
      ref={ref}
      {...reg}
      {...rest}
      error={invalid}
      helperText={invalid ? errorMessage : helperText}
    />
  );
}
